import { styled } from '@mui/material'
import {
  AnimatedSwitch,
  Section,
  RouterPanel,
  useActiveSectionsTop,
  useSectionHistory,
  useActiveSectionsHistory,
} from 'weblite-router'
import { usePrevious } from '$common/react-hooks/use-previous'
import { useCommSectionVisibilityStateInDesktopSize } from '$scenes/components/app/react-hooks'
import {
  CallPanel,
  CourseVitrinePanel,
  HomePanel,
  ProfilePanel,
  PvCallManagerPanel,
  RoomPanel,
  RoomProfilePanel,
  SJFSignupPanel,
  VitrinePanel,
  WappPanel,
} from '$scenes/router-panels'
import { useIsFullscreenEnabled } from '$logic/fullscreen/fullscreen-mode'

const SectionsContainer = styled('div')`
  display: flex;
  width: 100%;
  height: 100%;
`

const SectionContainer = styled('div')`
  min-width: 360px;
  width: 360px;
  overflow: hidden;

  &[data-grow='true'] {
    width: unset;
    flex-grow: 1;
  }

  &[data-collapse='true'] {
    min-width: 0;
    width: 0;
  }
`

const FullScreenSection = styled('div')`
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  display: none;
  z-index: 10;

  &[data-show='true'] {
    width: 100%;
    height: 100%;
    display: block;
  }
`

const CommSection = styled('div')`
  position: absolute;
  right: 0;
  top: 0;
  width: 0;
  height: 0;
  display: none;
  z-index: 10;

  &[data-show='true'] {
    width: 360px;
    height: 100%;
    display: block;

    &.fullscreen {
      width: 100%;
      left: 0;
      z-index: 13;
    }
  }
`

export function AppDesktop() {
  const activeSection = useActiveSectionsTop()
  const prevActiveSection = usePrevious(activeSection)

  const rightSectionHistory = useSectionHistory('right')

  const activeSections = useActiveSectionsHistory()
  const leftPartTopSection = activeSections.reduce(
    (acc, section) =>
      ['left', 'channelVitrine', 'courseVitrine'].includes(section)
        ? section
        : acc,
    '',
  )

  const leftPartActiveSectionIndex =
    leftPartTopSection === 'courseVitrine'
      ? 2
      : leftPartTopSection === 'channelVitrine'
        ? 1
        : 0

  const { show: showCommSection, fullscreen: commSectionIsFullScreen } =
    useCommSectionVisibilityStateInDesktopSize()

  const midSectionIsFullscreen = useIsFullscreenEnabled()

  return (
    <>
      <SectionsContainer>
        <SectionContainer
          sx={{
            borderLeft: '1px solid',
            borderColor: 'neutral300',
            transform: 'translateZ(0)',
          }}
        >
          <AnimatedSwitch
            keepAllMounted
            name="none"
            activeKey={leftPartActiveSectionIndex}
          >
            <div>
              <Section
                isInitialSection
                name="left"
                transition={{ name: 'none' }}
                initialPanel="home"
              >
                <ProfilePanel.Portal data-name="profile">
                  <RouterPanel />
                </ProfilePanel.Portal>

                <HomePanel.Portal data-name="home">
                  <RouterPanel />
                </HomePanel.Portal>

                <RoomProfilePanel.Portal data-name="roomProfile">
                  <RouterPanel />
                </RoomProfilePanel.Portal>
              </Section>
            </div>

            <div>
              <Section name="channelVitrine" initialPanel="vitrine">
                <VitrinePanel.Portal data-name="vitrine">
                  <RouterPanel />
                </VitrinePanel.Portal>
              </Section>
            </div>

            <div>
              <Section name="courseVitrine" initialPanel="courseVitrine">
                <CourseVitrinePanel.Portal data-name="courseVitrine">
                  <RouterPanel />
                </CourseVitrinePanel.Portal>
              </Section>
            </div>
          </AnimatedSwitch>
        </SectionContainer>

        <SectionContainer
          data-grow
          sx={{ zIndex: midSectionIsFullscreen ? 11 : undefined }}
        >
          <Section name="mid" initialPanel="room">
            <RoomPanel.Portal data-name="room">
              <RouterPanel />
            </RoomPanel.Portal>
          </Section>
        </SectionContainer>

        <SectionContainer
          data-collapse={!rightSectionHistory?.length}
          sx={{ borderRight: '1px solid', borderColor: 'neutral300' }}
        >
          <Section name="right">
            <WappPanel.Portal data-name="wapp">
              <RouterPanel />
            </WappPanel.Portal>
          </Section>
        </SectionContainer>
      </SectionsContainer>

      <CommSection
        data-show={showCommSection}
        className={commSectionIsFullScreen ? 'fullscreen' : ''}
      >
        <Section name="comm" initialPanel="call" transition={{ name: 'none' }}>
          <CallPanel.Portal data-name="call">
            <RouterPanel />
          </CallPanel.Portal>

          <PvCallManagerPanel.Portal data-name="pvCallManager">
            <RouterPanel />
          </PvCallManagerPanel.Portal>
        </Section>
      </CommSection>

      <FullScreenSection
        data-show={
          activeSection === 'sjf' ||
          (prevActiveSection === 'sjf' && activeSection === 'overlay')
        }
      >
        <Section name="sjf" initialPanel="sjfSignup">
          <SJFSignupPanel.Portal data-name="sjfSignup">
            <RouterPanel />
          </SJFSignupPanel.Portal>
        </Section>
      </FullScreenSection>
    </>
  )
}
